/**
 * Session Presenter Page (Phase 2 task 5)
 *
 * `/sessions/:id/present` — auth-gated route where a session creator (or any
 * server-authorised PRESENTER/BROADCASTER) connects to the LiveKit room and
 * controls their own publish state.
 *
 * Wired to:
 *   - `sessions.get`            — surface session metadata + ENDED state
 *   - `useLivekitToken`         — fetches a publisher token (GET /api/livekit/token)
 *   - `<LiveKitRoom>`           — opens the WebRTC connection
 *   - `useLocalParticipant()`   — toggle screen share / camera / mic
 *   - `useTracks()`             — preview the participant's own published tracks
 *
 * Screen share and camera are two mutually exclusive modes. LiveKit can
 * publish both at once (they are distinct sources, plan §4.3) and this page
 * used to, but MU asked for one at a time: to swap, you stop the current one
 * first (feedback 2026-08-05). The buttons therefore disable each other rather
 * than stacking.
 *
 * The microphone belongs to the camera mode. While sharing a screen the mic
 * toggle is hidden — MU did not want a presenter's voice riding along with a
 * slide deck — and desktop audio is requested from the browser instead, which
 * offers it in the picker where the platform supports it.
 */

import {
	LiveKitRoom,
	useConnectionState,
	useLocalParticipant,
	useRoomContext,
	useTracks,
	VideoTrack,
} from '@livekit/components-react'
import { ConnectionState, Track } from 'livekit-client'
import {
	ArrowLeft,
	Camera,
	CameraOff,
	ChevronDown,
	FlipHorizontal,
	Loader2,
	Mic,
	MicOff,
	Monitor,
	MonitorOff,
	Square,
	Users,
	Video,
	VideoOff,
	Wifi,
	WifiOff,
} from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { AuthGuard } from '../components/auth-guard'
import { QRCodeButton } from '../components/qr-code'
import { Alert, Button, buttonClasses, cn, EmptyState, Spinner } from '../components/ui'
import { ViewerBreadcrumb } from '../components/viewer/viewer-breadcrumb'
import { useLivekitToken } from '../hooks/use-livekit-token'
import { usePageTitle } from '../hooks/use-page-title'
import { useSessionRoomContext } from '../hooks/use-session-room-context'
import { Sentry } from '../observability/sentry'
import { trpc } from '../trpc/client'

function PresenterContent() {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const { id: sessionId } = useParams<{ id: string }>()

	const sessionQuery = trpc.sessions.get.useQuery(
		{ id: sessionId ?? '' },
		{ enabled: !!sessionId, refetchInterval: 30_000, refetchOnWindowFocus: true },
	)
	usePageTitle(sessionQuery.data?.title ?? t('sessions.list.title'))

	// A broadcast started from a room belongs to that room, not to the
	// Quick-services list it happens to share a route with. `sessions.get`
	// carries only `roomId`, so resolve the space through the room to build the
	// link back (MU 5. 8.).
	const room = useSessionRoomContext(sessionQuery.data?.roomId ?? null)

	const tokenResult = useLivekitToken(sessionId, { enabled: !!sessionId })

	// MP-194: live viewer count — viewer CONNECTIONS on this session right now
	// (WS transcript subscribers + LiveKit non-publishers), polled while live.
	const viewerCountQuery = trpc.sessions.viewerCount.useQuery(
		{ id: sessionId ?? '' },
		{
			enabled: !!sessionId && sessionQuery.data?.status === 'ACTIVE',
			refetchInterval: 15_000,
		},
	)

	// MP-194: expandable live viewer ROSTER (who, not just how many). Polled
	// only while the panel is open to avoid extra LiveKit calls when collapsed.
	const [showViewers, setShowViewers] = useState(false)
	const viewersQuery = trpc.sessions.viewers.useQuery(
		{ id: sessionId ?? '' },
		{
			enabled: !!sessionId && sessionQuery.data?.status === 'ACTIVE' && showViewers,
			refetchInterval: 15_000,
		},
	)

	const endMutation = trpc.sessions.end.useMutation({
		onSuccess: () => {
			navigate(room.href ?? '/sessions')
		},
	})

	const handleEndSession = useCallback(() => {
		if (!sessionId) return
		endMutation.mutate({ id: sessionId })
	}, [endMutation, sessionId])

	if (!sessionId) {
		return (
			<div className="flex items-center justify-center min-h-[60vh] p-4">
				<EmptyState
					icon={<VideoOff size={32} />}
					title={t('sessions.present.errors.missingId')}
					description={t('sessions.present.errors.missingIdDesc')}
					action={
						<Link to="/sessions" className={buttonClasses()}>
							{t('sessions.present.backToList')}
						</Link>
					}
				/>
			</div>
		)
	}

	if (sessionQuery.isLoading) {
		return (
			<div className="flex flex-col items-center justify-center min-h-[60vh] gap-3">
				<Loader2 size={32} className="animate-spin text-primary" />
				<p className="text-sm text-base-content/60">{t('sessions.present.loading')}</p>
			</div>
		)
	}

	if (sessionQuery.isError || !sessionQuery.data) {
		const message = sessionQuery.error?.message ?? t('sessions.present.errors.notFound')
		return (
			<div className="flex items-center justify-center min-h-[60vh] p-4">
				<EmptyState
					icon={<VideoOff size={32} />}
					title={t('sessions.present.errors.notFoundTitle')}
					description={message}
					action={
						<Link to="/sessions" className={buttonClasses()}>
							{t('sessions.present.backToList')}
						</Link>
					}
				/>
			</div>
		)
	}

	const session = sessionQuery.data
	const isEnded = session.status === 'ENDED'

	// MP-150: starting a web broadcast navigates the presenter here from the room
	// page — which was the only place the viewer QR code lived. Surface the
	// session's shareable join URL here too so the QR stays reachable while the
	// presentation is live. r65: prefer the dictatable short code when the
	// session has one; the cuid URL stays the fallback for pre-r65 sessions.
	//
	// A broadcast the server could link to a room screen service shares that
	// service's viewer link instead — the room page already hands out
	// /view/screen/:id, and a second /join URL for the same picture confused
	// people (MU 24. 8.). Unlinked broadcasts keep the /join fallback.
	const shareUrl = session.serviceId
		? `${window.location.origin}/view/screen/${session.serviceId}`
		: `${window.location.origin}/join/${session.shortCode ?? session.id}`

	if (isEnded) {
		return (
			<div className="flex items-center justify-center min-h-[60vh] p-4">
				<EmptyState
					icon={<VideoOff size={32} />}
					title={t('sessions.present.endedTitle')}
					description={t('sessions.present.endedDesc')}
					action={
						<Link to="/sessions" className={buttonClasses()}>
							{t('sessions.present.backToList')}
						</Link>
					}
				/>
			</div>
		)
	}

	// Phase 2 plan §10 task 5: render LiveKit scaffolding only when the token
	// resolves. Loading + error states stay outside the room so we don't open
	// a connection with a stale token. `connect={true}` is set inside the
	// room block so passing token+url alone won't start the WebRTC handshake.
	return (
		<div className="space-y-6 animate-enter">
			<div className="flex items-center gap-3 flex-wrap">
				<Link
					to={room.href ?? '/sessions'}
					className={cn(buttonClasses({ variant: 'ghost', size: 'sm' }), 'p-2 rounded-xl')}
					aria-label={t('common.back')}
				>
					<ArrowLeft size={20} />
				</Link>
				<div className="flex-1 min-w-0">
					{/* MU QA 26. 8.: this read as a hierarchy but was one link to the
					    room, so the space it names was unreachable. */}
					{room.breadcrumbRoom && <ViewerBreadcrumb room={room.breadcrumbRoom} linked />}
					<h1 className="text-2xl font-display font-semibold truncate">
						{t('sessions.present.title', { title: session.title })}
					</h1>
					<p className="text-base-content/60 mt-1">{t('sessions.present.subtitle')}</p>
				</div>
				{/* MP-194: the viewer count is now a toggle that reveals the live
				    roster (who is watching). No aria-live — a polling count would
				    spam screen readers; the tooltip carries the per-transport split. */}
				{viewerCountQuery.data && (
					<div className="relative">
						<button
							type="button"
							onClick={() => setShowViewers((v) => !v)}
							className={cn(
								buttonClasses({ variant: 'ghost', size: 'sm' }),
								'inline-flex items-center gap-1.5',
							)}
							aria-expanded={showViewers}
							title={t('sessions.present.viewers.tooltip', {
								transcript: viewerCountQuery.data.transcriptViewers,
								video: viewerCountQuery.data.videoViewers,
							})}
						>
							<Users size={14} aria-hidden="true" />
							{t('sessions.present.viewers.label', { total: viewerCountQuery.data.total })}
							<ChevronDown
								size={14}
								aria-hidden="true"
								className={cn('transition-transform', showViewers && 'rotate-180')}
							/>
						</button>
						{showViewers && (
							<div className="absolute right-0 z-20 mt-1 w-64 rounded-xl border border-base-300 bg-base-100 p-3 shadow-lg text-left">
								<p className="text-xs font-semibold text-base-content/70 mb-2">
									{t('sessions.present.viewers.rosterTitle')}
								</p>
								{!viewersQuery.data ? (
									<p className="text-sm text-base-content/60">{t('common.loading')}</p>
								) : viewersQuery.data.videoViewers.length === 0 &&
									viewersQuery.data.transcriptViewers.length === 0 ? (
									<p className="text-sm text-base-content/60">
										{t('sessions.present.viewers.none')}
									</p>
								) : (
									// One flat "who's watching" list (MP-194). A viewer consuming
									// both transports appears once per transport — consistent with
									// the "connections, not unique people" count semantics. Keys are
									// prefixed per transport since LiveKit identities and WS
									// connection ids live in separate namespaces.
									<ul className="space-y-1 max-h-60 overflow-y-auto">
										{viewersQuery.data.videoViewers.map((viewer) => (
											<li key={`video-${viewer.identity}`} className="text-sm truncate">
												{viewer.name ?? t('sessions.present.viewers.anonymous')}
											</li>
										))}
										{viewersQuery.data.transcriptViewers.map((viewer) => (
											<li key={`transcript-${viewer.identity}`} className="text-sm truncate">
												{viewer.name ?? t('sessions.present.viewers.anonymous')}
											</li>
										))}
									</ul>
								)}
							</div>
						)}
					</div>
				)}
				<QRCodeButton
					value={shareUrl}
					title={session.title}
					description={t('sessions.present.shareQrDescription')}
					variant="ghost"
					size="sm"
				/>
				<Button
					variant="ghost"
					onClick={handleEndSession}
					loading={endMutation.isPending}
					leadingIcon={<Square size={14} />}
					className="text-error"
				>
					{room.href ? t('sessions.present.endBroadcast') : t('sessions.present.endSession')}
				</Button>
			</div>

			{tokenResult.status === 'error' && (
				<Alert tone={tokenResult.disabled ? 'warning' : 'error'}>
					{tokenResult.disabled
						? t('sessions.present.errors.livekitDisabled')
						: tokenResult.unauthenticated
							? t('sessions.present.errors.notAuthorized')
							: tokenResult.error}
				</Alert>
			)}

			{(tokenResult.status === 'loading' || tokenResult.status === 'idle') && (
				<div className="card-refined p-8 flex flex-col items-center gap-3">
					<Spinner size="lg" />
					<p className="text-sm text-base-content/60">{t('sessions.present.connecting')}</p>
				</div>
			)}

			{tokenResult.status === 'ready' && tokenResult.token && tokenResult.url && (
				<LiveKitRoom
					token={tokenResult.token}
					serverUrl={tokenResult.url}
					connect={true}
					video={false}
					audio={false}
					onError={(err) => {
						// LiveKit fires synthetic events on every reconnect attempt;
						// log so devtools is useful but don't surface as a hard error.
						console.error('[LiveKit] presenter room error', err)
					}}
				>
					<PresenterControls isRoomBroadcast={!!room.href} />
				</LiveKitRoom>
			)}
		</div>
	)
}

function PresenterControls({ isRoomBroadcast }: { isRoomBroadcast: boolean }) {
	const { t } = useTranslation()
	const connectionState = useConnectionState()
	const room = useRoomContext()
	const { localParticipant } = useLocalParticipant()
	const tracks = useTracks(
		[Track.Source.ScreenShare, Track.Source.Camera, Track.Source.Microphone],
		{
			onlySubscribed: false,
		},
	)

	const localScreenTrack = tracks.find(
		(t) => t.participant.isLocal && t.source === Track.Source.ScreenShare,
	)
	const localCameraTrack = tracks.find(
		(t) => t.participant.isLocal && t.source === Track.Source.Camera,
	)
	// MP-190: with only one active track, the hard-coded two-column preview grid
	// left the lone tile pinned to the left column with a dead right half.
	const previewTrackCount = (localScreenTrack ? 1 : 0) + (localCameraTrack ? 1 : 0)

	const [screenSharing, setScreenSharing] = useState(false)
	const [cameraOn, setCameraOn] = useState(false)
	const [micOn, setMicOn] = useState(false)
	const [actionError, setActionError] = useState<string | null>(null)
	// `screenSharing` / `cameraOn` only settle once LiveKit has published and the
	// track effect has run, so two fast clicks could both read "nothing is on"
	// and publish both sources — exactly what the mutual exclusion is meant to
	// prevent. This lock rejects the second click while the first is in flight.
	const [switchingMode, setSwitchingMode] = useState(false)

	const isConnected = connectionState === ConnectionState.Connected
	const isConnecting = connectionState === ConnectionState.Connecting

	// Reflect user-cancelled screen share (browser "stop sharing" button) +
	// other external state changes back into the toggle UI.
	useEffect(() => {
		setScreenSharing(!!localScreenTrack)
	}, [localScreenTrack])
	useEffect(() => {
		setCameraOn(!!localCameraTrack)
	}, [localCameraTrack])

	// Live publication state, straight from LiveKit rather than from React state
	// that lags a render behind.
	const isPublished = useCallback(
		(source: Track.Source) => !!localParticipant?.getTrackPublication(source)?.track,
		[localParticipant],
	)

	const toggleScreenShare = useCallback(async () => {
		if (!localParticipant) return
		// One mode at a time (MU 5. 8.). The button is disabled while the camera
		// runs; these guards catch a click that raced the state update.
		if (switchingMode) return
		if (!screenSharing && isPublished(Track.Source.Camera)) return
		setActionError(null)
		setSwitchingMode(true)
		try {
			// `audio: true` makes the browser offer desktop/tab audio in its own
			// picker. Chrome offers it for a tab or the whole screen, Firefox and
			// Safari mostly do not — there is no way to force it, so the option
			// simply appears where the platform supports it.
			await localParticipant.setScreenShareEnabled(!screenSharing, {
				audio: true,
				// Slides and text: tell the encoder to keep sharpness (MU 9. 10.).
				contentHint: 'text',
			})
		} catch (err) {
			// `setScreenShareEnabled` rejects when the user cancels the picker
			// — surface anything unexpected, ignore the cancel.
			const message = err instanceof Error ? err.message : 'Screen share failed'
			if (!/Permission denied|cancelled|aborted/i.test(message)) {
				setActionError(message)
			}
		} finally {
			setSwitchingMode(false)
		}
	}, [localParticipant, screenSharing, isPublished, switchingMode])

	// livekit-client maps "disable" to MUTE for camera/mic (the publication
	// stays live, so viewers keep a black tile and the useTracks sync flips the
	// toggle straight back on) and to UNPUBLISH only for screen share. In a
	// broadcast tool "stop" must unpublish; `true` also stops the device so the
	// camera light goes off.
	const unpublishSource = useCallback(
		async (source: Track.Source) => {
			if (!localParticipant) return
			const publication = localParticipant.getTrackPublication(source)
			if (publication?.track) {
				await localParticipant.unpublishTrack(publication.track, true)
			}
		},
		[localParticipant],
	)

	const toggleCamera = useCallback(async () => {
		if (!localParticipant) return
		if (switchingMode) return
		if (!cameraOn && isPublished(Track.Source.ScreenShare)) return
		setActionError(null)
		setSwitchingMode(true)
		try {
			if (cameraOn) {
				// The mic is an option of the camera mode, so it goes with it —
				// otherwise a presenter who "stopped the camera" would still be
				// broadcasting audio.
				await unpublishSource(Track.Source.Camera)
				await unpublishSource(Track.Source.Microphone)
				setMicOn(false)
			} else {
				await localParticipant.setCameraEnabled(true)
			}
		} catch (err) {
			const message = err instanceof Error ? err.message : 'Camera toggle failed'
			setActionError(message)
		} finally {
			setSwitchingMode(false)
		}
	}, [localParticipant, cameraOn, isPublished, unpublishSource, switchingMode])

	// Front/back (or any other) camera. Populated only while publishing, because
	// enumerateDevices() hides labels until a capture permission is granted.
	const [videoInputs, setVideoInputs] = useState<MediaDeviceInfo[]>([])
	const [activeCameraId, setActiveCameraId] = useState<string | null>(null)

	useEffect(() => {
		if (!cameraOn || !navigator.mediaDevices?.enumerateDevices) {
			setVideoInputs([])
			return
		}
		let cancelled = false
		navigator.mediaDevices
			.enumerateDevices()
			.then((devices) => {
				if (!cancelled) setVideoInputs(devices.filter((d) => d.kind === 'videoinput'))
			})
			.catch(() => {
				// Device enumeration is a nice-to-have; failing it just hides the
				// switch button.
				if (!cancelled) setVideoInputs([])
			})
		return () => {
			cancelled = true
		}
	}, [cameraOn])

	/**
	 * Cycle to the next camera. A device that refuses to start (a virtual
	 * camera with no source — "Could not start video source") is skipped and
	 * the cursor moves on, so the button never gets stuck retrying the same
	 * broken device (MU 24. 8.). Each skip is reported, a permission refusal
	 * stops the cycle at once, and when every other camera fails the original
	 * one is restarted so the picture does not go black while the UI still
	 * names it. A single-camera device is a silent no-op — nothing to cycle to.
	 */
	const switchCamera = useCallback(async () => {
		if (videoInputs.length < 2 || switchingMode) return
		const currentId = activeCameraId ?? videoInputs[0]?.deviceId
		const index = videoInputs.findIndex((d) => d.deviceId === currentId)
		// An active id missing from the list (device unplugged, or a browser that
		// rotates ids between permission grants) means nothing is known to be
		// current — try every device instead of every device but one.
		const candidates =
			index === -1 ? videoInputs : videoInputs.slice(index + 1).concat(videoInputs.slice(0, index))
		setActionError(null)
		setSwitchingMode(true)
		try {
			for (const candidate of candidates) {
				try {
					await room.switchActiveDevice('videoinput', candidate.deviceId)
					setActiveCameraId(candidate.deviceId)
					return
				} catch (err) {
					const name = err instanceof Error ? err.name : 'unknown'
					if (name === 'NotAllowedError' || name === 'SecurityError') {
						setActionError(t('sessions.present.errors.cameraPermissionDenied'))
						return
					}
					Sentry.captureMessage(`Camera switch candidate failed: ${name}`, {
						level: 'warning',
						extra: {
							deviceId: candidate.deviceId,
							label: candidate.label,
							message: err instanceof Error ? err.message : String(err),
							candidates: candidates.length,
						},
					})
				}
			}
			// Every candidate failed. The failed restarts may have stopped the
			// original track, so bring it back before telling the presenter.
			if (currentId && index !== -1) {
				try {
					await room.switchActiveDevice('videoinput', currentId)
				} catch (restoreErr) {
					Sentry.captureMessage('Camera restore after failed switch failed', {
						level: 'error',
						extra: { deviceId: currentId, message: String(restoreErr) },
					})
					setActionError(t('sessions.present.errors.cameraLost'))
					return
				}
			}
			setActionError(t('sessions.present.errors.cameraSwitchAllFailed'))
		} finally {
			setSwitchingMode(false)
		}
	}, [room, videoInputs, activeCameraId, switchingMode, t])

	const toggleMic = useCallback(async () => {
		if (!localParticipant) return
		setActionError(null)
		try {
			await localParticipant.setMicrophoneEnabled(!micOn)
			setMicOn(!micOn)
		} catch (err) {
			const message = err instanceof Error ? err.message : 'Mic toggle failed'
			setActionError(message)
		}
	}, [localParticipant, micOn])

	const stopAll = useCallback(async () => {
		if (!localParticipant) return
		setActionError(null)
		try {
			await Promise.all([
				// screen share unpublishes natively (incl. its audio track)
				localParticipant.setScreenShareEnabled(false),
				unpublishSource(Track.Source.Camera),
				unpublishSource(Track.Source.Microphone),
			])
			setScreenSharing(false)
			setCameraOn(false)
			setMicOn(false)
		} catch (err) {
			const message = err instanceof Error ? err.message : 'Failed to stop tracks'
			setActionError(message)
		}
	}, [localParticipant, unpublishSource])

	const isPublishing = screenSharing || cameraOn

	return (
		<div className="space-y-6">
			<div className="card-refined p-4">
				<div className="flex items-center justify-between flex-wrap gap-3">
					<div className="flex items-center gap-2 text-sm">
						{isConnecting ? (
							<>
								<Spinner size="sm" />
								<span className="text-warning">{t('sessions.present.status.connecting')}</span>
							</>
						) : isConnected ? (
							<>
								<Wifi size={16} className="text-success" />
								<span className="text-success">{t('sessions.present.status.connected')}</span>
							</>
						) : (
							<>
								<WifiOff size={16} className="text-error" />
								<span className="text-error">{t('sessions.present.status.disconnected')}</span>
							</>
						)}
					</div>
				</div>
			</div>

			<div className="card-refined p-4 space-y-4">
				<h2 className="text-lg font-semibold">{t('sessions.present.controlsTitle')}</h2>
				<div className="flex flex-wrap gap-2">
					<Button
						variant={screenSharing ? 'accent' : 'primary'}
						onClick={toggleScreenShare}
						disabled={!isConnected || switchingMode || (!screenSharing && cameraOn)}
						title={
							!screenSharing && cameraOn ? t('sessions.present.hints.stopCameraFirst') : undefined
						}
						leadingIcon={screenSharing ? <MonitorOff size={16} /> : <Monitor size={16} />}
					>
						{screenSharing
							? t('sessions.present.actions.stopScreen')
							: t('sessions.present.actions.shareScreen')}
					</Button>
					<Button
						variant={cameraOn ? 'accent' : 'primary'}
						onClick={toggleCamera}
						disabled={!isConnected || switchingMode || (!cameraOn && screenSharing)}
						title={
							!cameraOn && screenSharing ? t('sessions.present.hints.stopScreenFirst') : undefined
						}
						leadingIcon={cameraOn ? <CameraOff size={16} /> : <Camera size={16} />}
					>
						{cameraOn
							? t('sessions.present.actions.stopCamera')
							: t('sessions.present.actions.shareCamera')}
					</Button>
					{cameraOn && videoInputs.length > 1 && (
						<Button
							variant="ghost"
							onClick={switchCamera}
							disabled={!isConnected || switchingMode}
							loading={switchingMode}
							leadingIcon={<FlipHorizontal size={16} />}
						>
							{t('sessions.present.actions.switchCamera')}
						</Button>
					)}
					{/* The mic is offered with the camera only. During a screen share
						MU wants desktop audio from the browser picker instead. */}
					{cameraOn && (
						<Button
							variant={micOn ? 'accent' : 'ghost'}
							onClick={toggleMic}
							disabled={!isConnected}
							leadingIcon={micOn ? <Mic size={16} /> : <MicOff size={16} />}
						>
							{micOn
								? t('sessions.present.actions.muteAudio')
								: t('sessions.present.actions.unmuteAudio')}
						</Button>
					)}
					{/* A room broadcast ends with "Ukončit vysílání" in the header;
					    offering "Zastavit vše" next to it read as two buttons for
					    one thing (MU 24. 8.). Ad-hoc sessions keep both: stopping
					    tracks and ending the session are different acts there. */}
					{isPublishing && !isRoomBroadcast && (
						<Button
							variant="ghost"
							onClick={stopAll}
							disabled={!isConnected}
							leadingIcon={<Square size={16} />}
							className="text-error"
						>
							{t('sessions.present.actions.stopAll')}
						</Button>
					)}
				</div>
				{actionError && <Alert tone="error">{actionError}</Alert>}
			</div>

			<div className="card-refined p-4 space-y-3">
				<h2 className="text-lg font-semibold flex items-center gap-2">
					<Video size={18} />
					{t('sessions.present.preview.title')}
				</h2>
				{!isPublishing ? (
					<EmptyState
						icon={<Video size={28} />}
						title={t('sessions.present.preview.emptyTitle')}
						description={t('sessions.present.preview.emptyDesc')}
					/>
				) : (
					<div
						className={`grid gap-3 ${
							previewTrackCount > 1
								? 'grid-cols-1 md:grid-cols-2'
								: 'grid-cols-1 max-w-3xl mx-auto w-full'
						}`}
					>
						{localScreenTrack && (
							<div className="bg-black rounded-lg overflow-hidden">
								<div className="px-3 py-1.5 bg-base-300/40 text-xs font-medium flex items-center gap-1.5">
									<Monitor size={12} />
									{t('sessions.present.preview.screenLabel')}
								</div>
								<VideoTrack
									trackRef={localScreenTrack}
									className="w-full aspect-video object-contain"
								/>
							</div>
						)}
						{localCameraTrack && (
							<div className="bg-black rounded-lg overflow-hidden">
								<div className="px-3 py-1.5 bg-base-300/40 text-xs font-medium flex items-center gap-1.5">
									<Camera size={12} />
									{t('sessions.present.preview.cameraLabel')}
								</div>
								<VideoTrack
									trackRef={localCameraTrack}
									className="w-full aspect-video object-cover"
								/>
							</div>
						)}
					</div>
				)}
			</div>
		</div>
	)
}

export default function SessionPresentPage() {
	return (
		<AuthGuard>
			<PresenterContent />
		</AuthGuard>
	)
}
